import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

export default function AuthPage() {
  const { login, signup } = useAuth();
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      if (mode === 'login') await login(form.email, form.password);
      else await signup(form.name, form.email, form.password);
    } catch (e) {
      setError(e.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const inp = {
    width: '100%',
    padding: '12px 16px',
    background: '#f8fafc',
    border: '1.5px solid #e2e8f0',
    borderRadius: 10,
    color: '#1e293b',
    fontSize: 14,
    fontFamily: "'Sora',sans-serif",
    outline: 'none',
    marginBottom: 14,
    transition: 'border-color 0.2s',
  };

  const label = {
    fontSize: 12,
    color: '#64748b',
    marginBottom: 6,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    display: 'block',
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        fontFamily: "'Sora','Inter',sans-serif",
        background: '#f8fafc',
      }}
    >
      {/* ── LEFT PANEL (branding) ─────────────────────────────── */}
      <div
        style={{
          flex: 1,
          background: 'linear-gradient(160deg,#1e1b4b 0%,#312e81 50%,#1e3a5f 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 40px',
          minWidth: 0,
        }}
      >
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <div
            style={{
              width: 72, height: 72,
              background: 'linear-gradient(135deg,#3b82f6,#6366f1)',
              borderRadius: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 38, margin: '0 auto 20px',
              boxShadow: '0 8px 32px rgba(99,102,241,0.4)',
            }}
          >
            📄
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px', marginBottom: 8 }}>
            FreePDFMaster.in
          </div>
          <div style={{ fontSize: 14, color: '#a5b4fc', fontWeight: 400, lineHeight: 1.6, maxWidth: 300 }}>
            Professional PDF editing tools, completely free and online.
          </div>
        </div>

        {/* Feature list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%', maxWidth: 340 }}>
          {[
            ['✏️', 'Edit PDF', 'Add text, images, shapes and annotations'],
            ['🗜️', 'Compress PDF', 'Reduce file size with custom target'],
            ['📝', 'Convert to Word', 'Export as DOCX with text extraction'],
            ['📊', 'Convert to Excel', 'Extract data to XLSX spreadsheet'],
            ['🔒', 'Protect PDF', 'Password-lock documents securely'],
            ['🔗', 'Merge PDFs', 'Combine multiple documents into one'],
          ].map(([ic, title, desc]) => (
            <div
              key={title}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 14,
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 12,
                padding: '14px 16px',
              }}
            >
              <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}>{ic}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0', marginBottom: 2 }}>{title}</div>
                <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.5 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 40, textAlign: 'center', fontSize: 12, color: '#6366f1' }}>
          Free plan · 5 exports/month · No credit card needed
        </div>
      </div>

      {/* ── RIGHT PANEL (form) ────────────────────────────────── */}
      <div
        style={{
          width: 480,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 44px',
          background: '#fff',
          borderLeft: '1px solid #e2e8f0',
          overflowY: 'auto',
        }}
      >
        <div style={{ width: '100%', maxWidth: 380 }}>
          {/* Header */}
          <div style={{ marginBottom: 32 }}>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', marginBottom: 6, letterSpacing: '-0.5px' }}>
              {mode === 'login' ? 'Welcome back 👋' : 'Create your account'}
            </h1>
            <p style={{ fontSize: 14, color: '#64748b', margin: 0, lineHeight: 1.6 }}>
              {mode === 'login'
                ? 'Sign in to continue to your PDF workspace.'
                : 'Get started for free — no credit card required.'}
            </p>
          </div>

          {/* Mode tabs */}
          <div
            style={{
              display: 'flex',
              background: '#f1f5f9',
              borderRadius: 12,
              padding: 4,
              marginBottom: 28,
            }}
          >
            {['login', 'signup'].map((m) => (
              <button
                key={m}
                onClick={() => { setMode(m); setError(''); }}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: 'none',
                  borderRadius: 9,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 700,
                  fontFamily: "'Sora',sans-serif",
                  transition: 'all 0.2s',
                  background: mode === m
                    ? '#fff'
                    : 'transparent',
                  color: mode === m ? '#1e293b' : '#94a3b8',
                  boxShadow: mode === m ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                {m === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {/* Error */}
          {error && (
            <div
              style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 10,
                padding: '12px 16px',
                color: '#dc2626',
                fontSize: 13,
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              ⚠️ {error}
            </div>
          )}

          {/* Form fields */}
          {mode === 'signup' && (
            <>
              <span style={label}>Full Name</span>
              <input
                style={inp}
                placeholder="John Smith"
                value={form.name}
                onChange={set('name')}
                onFocus={(e) => (e.target.style.borderColor = '#3b82f6')}
                onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
              />
            </>
          )}

          <span style={label}>Email Address</span>
          <input
            style={inp}
            type="email"
            placeholder="you@example.com"
            value={form.email}
            onChange={set('email')}
            onFocus={(e) => (e.target.style.borderColor = '#3b82f6')}
            onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
          />

          <span style={label}>Password</span>
          <input
            style={inp}
            type="password"
            placeholder="••••••••"
            value={form.password}
            onChange={set('password')}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            onFocus={(e) => (e.target.style.borderColor = '#3b82f6')}
            onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
          />

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: loading
                ? '#94a3b8'
                : 'linear-gradient(135deg,#3b82f6,#6366f1)',
              color: '#fff',
              border: 'none',
              borderRadius: 11,
              fontSize: 15,
              fontWeight: 700,
              fontFamily: "'Sora',sans-serif",
              cursor: loading ? 'not-allowed' : 'pointer',
              marginTop: 4,
              letterSpacing: 0.3,
              boxShadow: loading ? 'none' : '0 4px 16px rgba(59,130,246,0.3)',
              transition: 'all 0.2s',
            }}
          >
            {loading
              ? '⏳ Please wait…'
              : mode === 'login'
              ? '→ Sign In'
              : '→ Create Free Account'}
          </button>

          {/* Divider */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              margin: '24px 0',
            }}
          >
            <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
            <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>
              or continue as
            </span>
            <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
          </div>

          {/* Plan info */}
          <div
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              padding: '16px 18px',
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: '#6366f1',
                marginBottom: 10,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              FREE Plan includes
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 8,
              }}
            >
              {[
                '✅ Edit PDFs',
                '✅ Compress PDFs',
                '✅ Convert to Word',
                '✅ Convert to Excel',
                '✅ Merge PDFs',
                '✅ 5 exports/month',
              ].map((f) => (
                <div key={f} style={{ fontSize: 12, color: '#475569', fontWeight: 500 }}>
                  {f}
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              marginTop: 20,
              textAlign: 'center',
              fontSize: 11,
              color: '#94a3b8',
            }}
          >
            By signing in you agree to our Terms of Service & Privacy Policy
          </div>
        </div>
      </div>
    </div>
  );
}
